// ============================================================================
// PATIENT ALERTS — Alertas inteligentes del paciente
// Se muestran solo cuando hay algo que requiere atención
// ============================================================================
import { AlertTriangle, Info, XCircle } from 'lucide-react';
import type { PatientSummary } from '../../types/patient';

interface PatientAlertsProps {
  summary: PatientSummary | null;
  patientIsActive: boolean;
}

interface Alert {
  id: string;
  tone: 'danger' | 'warning' | 'info';
  icon: React.ReactNode;
  message: string;
}

export function PatientAlerts({ summary, patientIsActive }: PatientAlertsProps) {
  if (!summary) return null;

  const alerts: Alert[] = [];

  // Paciente sin centros activos
  if (patientIsActive && summary.activeSitesCount === 0) {
    alerts.push({
      id: 'no-active-sites',
      tone: 'danger',
      icon: <XCircle className="h-4 w-4" />,
      message: 'El paciente está activo globalmente pero no tiene ningún centro activo asignado.',
    });
  }

  // Recordatorios fallidos sin gestionar
  if (summary.failedRemindersUnmanaged > 0) {
    alerts.push({
      id: 'failed-reminders',
      tone: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      message: `${summary.failedRemindersUnmanaged} ${summary.failedRemindersUnmanaged === 1 ? 'recordatorio fallido' : 'recordatorios fallidos'} sin gestionar. Revisa la sección de recordatorios.`,
    });
  }

  // Deuda alta
  if (summary.totalDebt > 1000) {
    alerts.push({
      id: 'high-debt',
      tone: 'warning',
      icon: <AlertTriangle className="h-4 w-4" />,
      message: `Deuda pendiente alta: ${summary.totalDebt.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €.`,
    });
  }

  // Presupuestos enviados sin respuesta hace mucho
  if (summary.stalledSentBudgetsCount && summary.stalledSentBudgetsCount > 0) {
    alerts.push({
      id: 'stalled-budgets',
      tone: 'info',
      icon: <Info className="h-4 w-4" />,
      message: `${summary.stalledSentBudgetsCount} ${summary.stalledSentBudgetsCount === 1 ? 'presupuesto enviado' : 'presupuestos enviados'} hace más de 30 días sin respuesta.`,
    });
  }

  if (alerts.length === 0) return null;

  const toneClasses: Record<Alert['tone'], string> = {
    danger: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-amber-50 border-amber-200 text-amber-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  return (
    <div className="space-y-2">
      {alerts.map((a) => (
        <div
          key={a.id}
          className={`flex items-start gap-3 px-4 py-2.5 rounded-lg border ${toneClasses[a.tone]}`}
          role="alert"
        >
          <div className="shrink-0 mt-0.5">{a.icon}</div>
          <p className="text-sm">{a.message}</p>
        </div>
      ))}
    </div>
  );
}

export default PatientAlerts;
